import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const LaborByCoatManagement = () => {
  const [laborCosts, setLaborCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceYear, setCopySourceYear] = useState(null);
  const [copyTargetYear, setCopyTargetYear] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);
  const [systemSettings, setSystemSettings] = useState({
    defaultCurrency: "CAD",
    preferredUnit: "sqm",
  });
  const [filters, setFilters] = useState({
    coat_type: "",
    is_active: "",
  });

  const [formData, setFormData] = useState({
    coatType: "prime",
    costPerUnit: "",
    unitOfMeasure: "sqm",
    year: new Date().getFullYear(),
    currency: "CAD",
    notes: "",
    isActive: true,
  });

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await api.get("/settings/categories");
      if (response.data.success) {
        const settings = response.data.data;
        const currencyUnits = settings.currency_units || {};
        const defaultCurrency = currencyUnits.default_currency?.value || "CAD";
        const preferredUnit = currencyUnits.preferred_unit?.value || "m²";

        const unitOfMeasure =
          preferredUnit.toLowerCase().includes("m²") ||
          preferredUnit.toLowerCase().includes("m2")
            ? "sqm"
            : "sqft";

        setSystemSettings({
          defaultCurrency,
          preferredUnit: unitOfMeasure,
        });

        setFormData((prev) => ({
          ...prev,
          currency: defaultCurrency,
          unitOfMeasure: unitOfMeasure,
        }));
      }
    } catch (err) {
      console.error("Error fetching system settings:", err);
    }
  }, []);

  const fetchLaborCosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/labor-by-coat?limit=500");
      if (response.data.success) {
        const rows = response.data.data || [];
        setLaborCosts(rows);
        setExpandedYears((prev) => {
          if (rows.length === 0 || prev.size > 0) return prev;
          const firstYear = [
            ...new Set(
              rows
                .map((row) => Number(row.year))
                .filter((y) => Number.isFinite(y)),
            ),
          ].sort((a, b) => b - a)[0];
          return firstYear ? new Set([firstYear]) : prev;
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch labor costs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemSettings();
    fetchLaborCosts();
  }, [fetchSystemSettings, fetchLaborCosts]);

  const resetForm = () => {
    setFormData({
      coatType: "prime",
      costPerUnit: "",
      unitOfMeasure: systemSettings.preferredUnit || "sqm",
      year: new Date().getFullYear(),
      currency: systemSettings.defaultCurrency || "CAD",
      notes: "",
      isActive: true,
    });
    setEditingCost(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (editingCost) {
        await api.put(`/labor-by-coat/${editingCost.id}`, formData);
      } else {
        await api.post("/labor-by-coat", formData);
      }

      setShowModal(false);
      resetForm();
      fetchLaborCosts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save labor cost");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      coatType: cost.coatType,
      costPerUnit: cost.costPerUnit,
      unitOfMeasure:
        cost.unitOfMeasure ||
        (cost.unitType
          ? cost.unitType.toLowerCase().includes("m²") ||
            cost.unitType.toLowerCase().includes("m2")
            ? "sqm"
            : "sqft"
          : "sqm"),
      year: cost.year,
      currency: cost.currency || "CAD",
      notes: cost.notes || "",
      isActive: cost.isActive !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (cost) => {
    if (!window.confirm(`Delete labor cost for ${cost.coatType}?`)) return;
    try {
      await api.delete(`/labor-by-coat/${cost.id}`);
      fetchLaborCosts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete");
    }
  };

  const handleDeleteLaborYear = async (year) => {
    const yearGroup = yearGroups.find((g) => g.year === year);
    const rows = (yearGroup && yearGroup.rows) || [];
    if (!rows.length) {
      alert(`No labor by coat rows found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${rows.length} labor by coat row(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        rows.map((cost) => api.delete(`/labor-by-coat/${cost.id}`)),
      );
      fetchLaborCosts();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to delete labor by coat rows for year",
      );
    }
  };

  const handleToggleActive = async (cost) => {
    try {
      await api.put(`/labor-by-coat/${cost.id}`, {
        isActive: !cost.isActive,
      });
      fetchLaborCosts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const getCoatTypeLabel = (type) => {
    const labels = {
      prime: "Prime Coat",
      stripe: "Stripe Coat",
      midcoat: "Midcoat",
      topcoat: "Topcoat",
    };
    return labels[type] || type;
  };

  const getCoatTypeColor = (type) => {
    const colors = {
      prime: "bg-purple-100 text-purple-800",
      stripe: "bg-orange-100 text-orange-800",
      midcoat: "bg-blue-100 text-blue-800",
      topcoat: "bg-green-100 text-green-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const filteredCosts = useMemo(() => {
    return laborCosts.filter((cost) => {
      const coatTypeMatch =
        !filters.coat_type || cost.coatType === filters.coat_type;
      const statusMatch =
        filters.is_active === "" ||
        String(cost.isActive !== false) === filters.is_active;
      return coatTypeMatch && statusMatch;
    });
  }, [laborCosts, filters]);

  const allYears = useMemo(
    () =>
      [
        ...new Set(
          laborCosts
            .map((row) => Number(row.year))
            .filter((y) => Number.isFinite(y)),
        ),
      ].sort((a, b) => b - a),
    [laborCosts],
  );

  const yearGroups = useMemo(() => {
    const grouped = new Map();
    filteredCosts.forEach((row) => {
      const year = Number(row.year);
      if (!Number.isFinite(year)) return;
      if (!grouped.has(year)) grouped.set(year, { year, rows: [] });
      grouped.get(year).rows.push(row);
    });
    return [...grouped.values()]
      .sort((a, b) => b.year - a.year)
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) =>
          getCoatTypeLabel(a.coatType).localeCompare(
            getCoatTypeLabel(b.coatType),
          ),
        ),
      }));
  }, [filteredCosts]);

  const toggleYear = (year) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const convertRowToPayload = (row, targetYear) => ({
    coatType: row.coatType,
    costPerUnit: row.costPerUnit,
    unitOfMeasure: row.unitOfMeasure || "sqm",
    year: targetYear,
    currency: row.currency || "CAD",
    notes: row.notes || "",
    isActive: row.isActive !== false,
  });

  const copyRowsToYear = async (sourceYear, targetYear) => {
    const fromYear = Number(sourceYear);
    const toYear = Number(targetYear);
    if (!Number.isFinite(fromYear) || !Number.isFinite(toYear)) return;

    const sourceRows = laborCosts.filter(
      (row) => Number(row.year) === fromYear,
    );
    if (sourceRows.length === 0) {
      setError(`No data found for source year ${fromYear}`);
      return;
    }

    const targetRows = laborCosts.filter((row) => Number(row.year) === toYear);
    if (targetRows.length > 0) {
      setError(
        `Target year ${toYear} already has records. Delete those rows first to avoid duplicates.`,
      );
      return;
    }

    setCopying(true);
    setCopyError("");
    try {
      await Promise.all(
        sourceRows.map((row) =>
          api.post("/labor-by-coat", convertRowToPayload(row, toYear)),
        ),
      );
      setShowCopyModal(false);
      setCopySourceYear(null);
      setCopyTargetYear("");
      fetchLaborCosts();
      setExpandedYears((prev) => {
        const next = new Set(prev);
        next.add(toYear);
        return next;
      });
    } catch (err) {
      setCopyError(err.response?.data?.message || "Failed to copy rows");
    } finally {
      setCopying(false);
    }
  };

  const handleImportPreviousYear = async (targetYear) => {
    const sortedOlderYears = allYears
      .filter((year) => year < targetYear)
      .sort((a, b) => b - a);
    const previousYear = sortedOlderYears[0];
    if (!previousYear) {
      setError(`No previous year data found to import into ${targetYear}`);
      return;
    }
    if (!window.confirm(`Import data from ${previousYear} into ${targetYear}?`))
      return;
    await copyRowsToYear(previousYear, targetYear);
  };

  const openCopyModal = (sourceYear) => {
    setCopySourceYear(sourceYear);
    setCopyTargetYear(String(Number(sourceYear) + 1));
    setCopyError("");
    setShowCopyModal(true);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage field application labor costs specific to each coat type
          (Prime, Stripe, Midcoat, Topcoat). Based on Excel Field Application
          Cost per coat table.
        </p>
        <InlineFieldTooltip
          page="labor_by_coat"
          section="page_description"
          fieldKey="labor_by_coat_overview"
        />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coat Type
            </label>
            <select
              name="coat_type"
              value={filters.coat_type}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, coat_type: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="prime">Prime</option>
              <option value="stripe">Stripe</option>
              <option value="midcoat">Midcoat</option>
              <option value="topcoat">Topcoat</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="is_active"
              value={filters.is_active}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, is_active: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ coat_type: "", is_active: "" })}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Labor Cost
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : yearGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No labor by coat records found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rows
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coat Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {yearGroups.map((yearGroup) => {
                  const isExpanded = expandedYears.has(yearGroup.year);
                  return (
                    <React.Fragment key={yearGroup.year}>
                      <tr className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            onClick={() => toggleYear(yearGroup.year)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {yearGroup.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {yearGroup.rows.length}
                        </td>
                        <td
                          className="px-6 py-4 text-sm text-gray-500"
                          colSpan={3}
                        >
                          Year group
                        </td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleImportPreviousYear(yearGroup.year)
                              }
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                            >
                              Import
                            </button>
                            <button
                              type="button"
                              onClick={() => openCopyModal(yearGroup.year)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteLaborYear(yearGroup.year)}
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all labor by coat rows for ${yearGroup.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded &&
                        yearGroup.rows.map((cost) => (
                          <tr key={cost.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4"></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {cost.year}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              -
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getCoatTypeColor(cost.coatType)}`}
                              >
                                {getCoatTypeLabel(cost.coatType)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                ${parseFloat(cost.costPerUnit).toFixed(2)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cost.currency || "CAD"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cost.unitOfMeasure === "sqm"
                                ? "m²"
                                : cost.unitOfMeasure === "sqft"
                                  ? "ft²"
                                  : cost.unitOfMeasure || cost.unitType || "m²"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={cost.isActive !== false}
                                onClick={() => handleToggleActive(cost)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  cost.isActive !== false
                                    ? "bg-blue-600"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    cost.isActive !== false
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEdit(cost)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </button>
                              <button
                                onClick={() => handleDelete(cost)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCopyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-3">
              <span>Copy Labor By Coat Rows To New Year</span>
              <InlineFieldTooltip
                page="labor_by_coat"
                section="modal_titles"
                fieldKey="copy_to_year"
                fallbackText="Copy labor by coat rows from one year to another."
                title="Copy Labor By Coat Rows"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Source year:{" "}
              <span className="font-semibold">{copySourceYear}</span>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyTargetYear}
              onChange={(e) => setCopyTargetYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {copyError && (
              <p className="mt-2 text-sm text-red-600">{copyError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopySourceYear(null);
                  setCopyTargetYear("");
                  setCopyError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => copyRowsToYear(copySourceYear, copyTargetYear)}
                disabled={copying}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {copying ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingCost ? "Edit Labor Cost" : "Add New Labor Cost"}
                </span>
                <InlineFieldTooltip
                  page="labor_by_coat"
                  section="modal_titles"
                  fieldKey="labor_cost_form"
                  fallbackText="Create or edit labor cost rows by coat type."
                  title="Labor Cost Form"
                />
              </h3>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coat Type *
                  </label>
                  <select
                    value={formData.coatType}
                    onChange={(e) =>
                      setFormData({ ...formData, coatType: e.target.value })
                    }
                    required
                    disabled={!!editingCost}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="prime">Prime Coat</option>
                    <option value="stripe">Stripe Coat</option>
                    <option value="midcoat">Midcoat</option>
                    <option value="topcoat">Topcoat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost per Unit *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costPerUnit}
                    onChange={(e) =>
                      setFormData({ ...formData, costPerUnit: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 6.24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency *
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit of Measure *
                  </label>
                  <select
                    value={formData.unitOfMeasure}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        unitOfMeasure: e.target.value,
                      })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sqm">sqm (m²)</option>
                    <option value="sqft">sqft (ft²)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year *
                  </label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        year:
                          parseInt(e.target.value, 10) ||
                          new Date().getFullYear(),
                      })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional notes..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Saving..." : editingCost ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaborByCoatManagement;
